import { useState } from "react";
import {
  DOC_CATEGORY_LABELS,
  REQUIREMENT_STATE_LABELS,
  USAGE_FUNCTION_LABELS,
  type DocumentTypeDto,
  type DossierDto,
  type DossierRequirementDto,
  type RequirementState,
} from "@klantpunt/shared";
import { Modal } from "../../app/Modal";
import { IconPencil, IconUpload } from "../../app/icons";
import { api } from "../../lib/api";
import { useToast } from "../../lib/toast";
import { errMsg, TypeSelect } from "./DossierDialogs";
import { usageText } from "./UsageFunctions";
import { fmtDate, useDossierRefresh } from "./useDossier";

const STATE_CLASS: Record<RequirementState, string> = {
  missing: "expired",
  ok: "ok",
  expiring: "warn",
  expired: "expired",
  not_applicable: "na",
};

export function RequirementChip({ state }: { state: RequirementState }) {
  return <span className={`chip dir-cert dir-cert-${STATE_CLASS[state]}`}>{REQUIREMENT_STATE_LABELS[state]}</span>;
}

/**
 * Tab Verplicht: welke documenten bij de gebruiksfunctie(s) van dit gebouw horen, en of ze er zijn. Per soort:
 * uploaden, niet van toepassing verklaren (met reden), of een extra soort verplicht maken.
 */
export function RequirementsView({
  dossier,
  buildingId,
  canEdit,
  types,
  onUpload,
  onUsage,
  onOpen,
}: {
  dossier: DossierDto;
  buildingId: string;
  canEdit: boolean;
  types: DocumentTypeDto[];
  onUpload: (typeKey: string) => void;
  onUsage: () => void;
  onOpen: (docId: string) => void;
}) {
  const toast = useToast();
  const refresh = useDossierRefresh(buildingId);
  const [na, setNa] = useState<DossierRequirementDto | null>(null);
  const [adding, setAdding] = useState(false);
  const reqs = dossier.requirements;
  const set = async (typeId: string, state: "required" | "not_applicable" | null, note?: string) => {
    try {
      await api(`/buildings/${buildingId}/dossier/requirements/${typeId}`, {
        method: "PUT",
        body: { state, note: note ?? null },
      });
      await refresh();
      toast(
        state === "not_applicable" ? "Niet van toepassing" : state === "required" ? "Verplicht gemaakt" : "Bijgewerkt",
      );
    } catch (err) {
      toast(errMsg(err));
    }
  };
  const open = reqs.filter((r) => r.state !== "ok" && r.state !== "not_applicable").length;
  const present = reqs.filter((r) => r.state === "ok").length;
  const counted = reqs.filter((r) => r.state !== "not_applicable").length;
  return (
    <section className="doc-reqs" aria-label="Verplichte documenten">
      <div className="doc-usage panel">
        <div className="grow">
          <p className="label">Gebruiksfunctie</p>
          <p>
            {dossier.usageFunctions.length ? (
              usageText(dossier.usageFunctions)
            ) : (
              <span className="muted">
                Nog niet ingevuld. Kies de gebruiksfunctie om te zien welke documenten horen.
              </span>
            )}
          </p>
          {counted > 0 && (
            <p className="muted small">
              {present} van {counted} aanwezig{open > 0 && ` · ${open} vraagt aandacht`}
            </p>
          )}
        </div>
        {canEdit && (
          <button type="button" className="btn small" onClick={onUsage}>
            <IconPencil size={18} aria-hidden="true" /> Wijzigen
          </button>
        )}
      </div>
      {reqs.length > 0 && (
        <div className="tscroll">
          <table className="it" aria-label="Verplichte documenten">
            <thead>
              <tr>
                <th scope="col">Document</th>
                <th scope="col" className="dir-hide-narrow">
                  Waarom
                </th>
                <th scope="col">Status</th>
              </tr>
            </thead>
            <tbody>
              {reqs.map((r) => (
                <tr key={r.type.id} className={r.state === "not_applicable" ? "muted" : ""}>
                  <td className="tcell-title">
                    <strong>{r.type.name}</strong>
                    <div className="muted small">
                      {DOC_CATEGORY_LABELS[r.type.category]}
                      {r.document && (
                        <>
                          {" · "}
                          <button type="button" className="linkbtn inline" onClick={() => onOpen(r.document!.id)}>
                            {r.document.title}
                          </button>
                          {r.document.validUntil && ` · tot ${fmtDate(r.document.validUntil)}`}
                        </>
                      )}
                      {r.note && ` · ${r.note}`}
                    </div>
                    {canEdit && (
                      <div className="doc-reqacts">
                        {r.state === "not_applicable" ? (
                          <button type="button" className="btn small ghost" onClick={() => void set(r.type.id, null)}>
                            Toch nodig
                          </button>
                        ) : (
                          <>
                            {r.state !== "ok" && r.type.key && (
                              <button type="button" className="btn small" onClick={() => onUpload(r.type.key!)}>
                                <IconUpload size={16} aria-hidden="true" /> Uploaden
                              </button>
                            )}
                            {r.source === "manual" ? (
                              <button
                                type="button"
                                className="btn small ghost"
                                onClick={() => void set(r.type.id, null)}
                              >
                                Niet meer verplicht
                              </button>
                            ) : (
                              r.state === "missing" && (
                                <button type="button" className="btn small ghost" onClick={() => setNa(r)}>
                                  Niet van toepassing
                                </button>
                              )
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="dir-hide-narrow small">
                    {r.source === "manual"
                      ? "Toegevoegd voor dit gebouw"
                      : r.functions.map((f) => USAGE_FUNCTION_LABELS[f]).join(", ")}
                  </td>
                  <td>
                    <RequirementChip state={r.state} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {canEdit && (
        <button type="button" className="btn small ghost" onClick={() => setAdding(true)}>
          + Soort verplicht maken voor dit gebouw
        </button>
      )}
      {na && (
        <NaDialog
          req={na}
          onClose={() => setNa(null)}
          onSave={async (note) => {
            await set(na.type.id, "not_applicable", note);
            setNa(null);
          }}
        />
      )}
      {adding && (
        <AddDialog
          types={types.filter((t) => !reqs.some((r) => r.type.id === t.id))}
          onClose={() => setAdding(false)}
          onSave={async (typeId) => {
            await set(typeId, "required");
            setAdding(false);
          }}
        />
      )}
    </section>
  );
}

function NaDialog({
  req,
  onClose,
  onSave,
}: {
  req: DossierRequirementDto;
  onClose: () => void;
  onSave: (note: string) => Promise<void>;
}) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <Modal
      title="Niet van toepassing"
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button
            type="button"
            className="btn primary"
            disabled={busy || !note.trim()}
            onClick={() => {
              setBusy(true);
              void onSave(note.trim()).finally(() => setBusy(false));
            }}
          >
            Opslaan
          </button>
        </>
      }
    >
      <p className="small">
        {req.type.name} is voor dit gebouw niet nodig. Leg kort uit waarom, zodat een collega of auditor het later
        begrijpt.
      </p>
      <label className="field">
        <span className="label">Reden *</span>
        <input
          value={note}
          maxLength={300}
          onChange={(e) => setNote(e.target.value)}
          placeholder="bijv. minder dan 50 personen tegelijk aanwezig"
        />
      </label>
    </Modal>
  );
}

function AddDialog({
  types,
  onClose,
  onSave,
}: {
  types: DocumentTypeDto[];
  onClose: () => void;
  onSave: (typeId: string) => Promise<void>;
}) {
  const [typeId, setTypeId] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <Modal
      title="Soort verplicht maken"
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button
            type="button"
            className="btn primary"
            disabled={busy || !typeId}
            onClick={() => {
              setBusy(true);
              void onSave(typeId).finally(() => setBusy(false));
            }}
          >
            Verplicht maken
          </button>
        </>
      }
    >
      <label className="field" htmlFor="req-add-type">
        <span className="label">Documentsoort</span>
        <TypeSelect id="req-add-type" types={types} value={typeId} onChange={setTypeId} only="document" />
        <span className="hint">Alleen voor dit gebouw. Ontbreekt het document, dan staat het hier als Ontbreekt.</span>
      </label>
    </Modal>
  );
}
